import { useStores } from 'hooks/useStores'
import trim from 'lodash/trim'
import { observer } from 'mobx-react'
import { useFormContext } from 'react-hook-form'
import { Col, Row } from 'reactstrap'
import { Where } from 'types/common/query'
import { IUser } from 'interfaces/user'
import Input from '../InputWithLabel'
import ProfilePictureSelector from '../ProfilePictureSelector'
import styles from './personalInformationSection.module.scss'
import { isValidEmail } from 'utils/common'

interface IPersonalInformationSectionProps {
  userId: number
}

const PersonalInformationSection = ({ userId }: IPersonalInformationSectionProps) => {
  const { userStore, authStore, organizationStore } = useStores()
  const { selectedUserDetail } = userStore
  const { organization } = organizationStore
  const isMyAccount = userId === authStore.userDetail.id
  const userDetail = isMyAccount ? userStore.userDetail : selectedUserDetail
  // In the profile-centric model every User owns their own credentials and email;
  // the legacy "youth-profile-session" exemption (where a child shared a guardian's
  // login) no longer exists, so this flag is permanently false.
  const isYouthProfileSession = false
  const currentEmail = userDetail?.email
  const currentUsername = userDetail?.username
  const { register } = useFormContext()
  const requiredMessage = 'This field is required'
  const members = userDetail?.groupMembers ?? []
  const groups = members?.map(member => member?.group?.name ?? '')

  return (
    <div>
      <h2 className={styles.title}>Personal information</h2>
      <Row>
        <Col xs="6" sm="4">
          <ProfilePictureSelector userId={userId} />
        </Col>

        <Col className={styles.wrapper}>
          <Input {...register('role')} disabled title="Current role" noPaddingTop />
          <Input {...register('fullName')} disabled placeholder="Your full name" title="Full name" />
          <Input
            {...register('email', {
              validate: {
                isEmail: (value: string | undefined) => {
                  const s = trim(value ?? '')
                  if (!s) return isYouthProfileSession ? true : isValidEmail(s)
                  return isValidEmail(s)
                },
                checkExists: async (value: string | undefined) => {
                  if (value) {
                    const filter: Where<IUser> = {
                      email: { eq: trim(value) },
                      organizationId: organization?.id ?? 0
                    }
                    const numberOfUser: number = await userStore.checkExistUser(filter)
                    return numberOfUser > 0 && value !== currentEmail ? 'Email exists' : true
                  }
                  return true
                }
              },
              ...(isYouthProfileSession ? {} : { required: requiredMessage })
            })}
            disabled
            placeholder="Your email"
            title="Email"
          />
          <Input
            {...register('username', {
              validate: {
                checkExists: async (value: string | undefined) => {
                  if (value) {
                    const filter: Where<IUser> = {
                      username: { eq: trim(value) },
                      organizationId: authStore.activeOrgId ?? 0
                    }
                    const numberOfUser: number = await userStore.checkExistUser(filter)
                    return numberOfUser > 0 && value !== currentUsername ? 'Username exists' : true
                  }
                  return true
                }
              },
              ...(isYouthProfileSession ? {} : { required: requiredMessage })
            })}
            disabled
            placeholder="Your username"
            title="Username"
          />
          {groups.length ? (
            <Col className={styles.groupWrapper}>
              <Row>
                <span className={styles.label}>Groups</span>
              </Row>
              <Row className={styles.colGroup}>
                {groups.map(group => (
                  <span className={styles.groupTag}>{group}</span>
                ))}
              </Row>
            </Col>
          ) : null}
        </Col>
      </Row>
    </div>
  )
}

export default observer(PersonalInformationSection)
